/* RevisionComparison — Step 9 Feature 2 — side-by-side iteration view.
 *
 * Dedicated page (separate from the small in-modal diff). Picks any
 * two revisions (or "current state") via dropdowns, displays:
 *   • KPI side-by-side table with % change badges
 *   • Cost section bars (A vs B) with delta highlight
 *   • Compliance check (FSI / Coverage / Setbacks) PASS-FAIL per side
 *   • Stage progress strip
 *   • Export → comparison PDF
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, GitCompare, FileText, Loader2,
  CheckCircle2, XCircle, ArrowDown, ArrowUp, Minus,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function _fmt(v) {
  if (v == null) return "—";
  if (typeof v === "number") {
    if (Math.abs(v) >= 1e7) return `${(v / 1e7).toFixed(2)} Cr`;
    if (Math.abs(v) >= 1e5) return `${(v / 1e5).toFixed(2)} L`;
    return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
}

function DeltaBadge({ pct }) {
  if (pct == null) return (
    <span style={{ color: "#64748b", fontSize: 11 }}>
      <Minus size={10}/> —
    </span>);
  if (pct === 0) return (
    <span style={{ color: "#94a3b8", fontSize: 11 }}>
      <Minus size={10}/> 0%
    </span>);
  const up = pct > 0;
  return (
    <span style={{
      color: up ? "#fbbf24" : "#67e8f9", fontSize: 11,
      display: "inline-flex", alignItems: "center", gap: 2,
    }}>
      {up ? <ArrowUp size={10}/> : <ArrowDown size={10}/>}
      {Math.abs(pct).toFixed(1)}%
    </span>);
}

export default function RevisionComparison() {
  const { id: projectId } = useParams();
  const [revisions, setRevisions] = useState([]);
  const [revA, setRevA] = useState("current");
  const [revB, setRevB] = useState("current");
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/api/projects/${projectId}/revisions`,
                  { withCredentials: true })
      .then((r) => {
        const list = r.data?.revisions || [];
        setRevisions(list);
        if (list[0]) setRevA(list[0].id);
        if (list[1]) setRevB(list[1].id);
        else if (list[0]) setRevB("current");
      });
  }, [projectId]);

  const run = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.post(
        `${API}/api/projects/${projectId}/comparison`,
        { rev_a_id: revA, rev_b_id: revB },
        { withCredentials: true });
      setComparison(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [projectId, revA, revB]);

  useEffect(() => { run(); /* auto-run on dropdown change */ },
                [run]);

  const dlPdf = () => {
    // Build a temporary form to POST + download
    fetch(`${API}/api/projects/${projectId}/comparison/pdf`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rev_a_id: revA, rev_b_id: revB }),
    }).then((res) => res.blob()).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "revision-comparison.pdf";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const labelFor = (id) => {
    if (id === "current") return "Current state";
    const r = revisions.find((x) => x.id === id);
    return r ? `#${r.revision_number} · ${r.label}` : id;
  };

  return (
    <div style={{ background: "#020617", minHeight: "100vh",
                    color: "#e2e8f0", padding: "24px 32px" }}
        data-testid="revision-comparison-page">
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        marginBottom: 20,
      }}>
        <Link to={`/projects/${projectId}/dashboard`}
            style={{ color: "#94a3b8",
                      display: "inline-flex", alignItems: "center",
                      gap: 6, fontSize: 13,
                      textDecoration: "none" }}>
          <ArrowLeft size={14}/> Dashboard
        </Link>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700,
                      flex: 1 }}>
          <GitCompare size={16}
              style={{ verticalAlign: -2, marginRight: 8 }}/>
          Revision Comparison
        </h1>
        <button onClick={dlPdf}
            data-testid="comparison-download-pdf"
            disabled={!comparison}
            style={{
              padding: "6px 14px", borderRadius: 4,
              background: "#7c3aed", color: "#fff",
              border: "1px solid #6d28d9", fontSize: 12,
              fontWeight: 600, cursor: "pointer",
              opacity: comparison ? 1 : 0.5,
            }}>
          <FileText size={12}/> Export PDF
        </button>
      </div>

      {/* Selectors */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16,
                      flexWrap: "wrap" }}>
        {["A", "B"].map((side, idx) => {
          const value = idx === 0 ? revA : revB;
          const setter = idx === 0 ? setRevA : setRevB;
          return (
            <div key={side} style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 10, color: "#94a3b8",
                              textTransform: "uppercase",
                              letterSpacing: 0.6,
                              marginBottom: 4, fontWeight: 700 }}>
                Side {side}
              </div>
              <select value={value}
                  onChange={(e) => setter(e.target.value)}
                  data-testid={`comparison-select-${side.toLowerCase()}`}
                  style={{
                    width: "100%", padding: 8, fontSize: 13,
                    background: "#0f172a", color: "#e2e8f0",
                    border: "1px solid #334155", borderRadius: 4,
                  }}>
                <option value="current">Current state</option>
                {revisions.map((r) => (
                  <option key={r.id} value={r.id}>
                    #{r.revision_number} · {r.label}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      {loading && (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>
          <Loader2 size={12}
              style={{ animation: "spin 1s linear infinite",
                        verticalAlign: -1, marginRight: 4 }}/>
          Computing comparison…
        </div>
      )}
      {error && (
        <div style={{ color: "#fca5a5", fontSize: 12 }}>
          {String(error).slice(0, 200)}
        </div>
      )}

      {comparison && (
        <div style={{ display: "grid", gap: 16,
                        gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)" }}>
          <div style={{ display: "flex", flexDirection: "column",
                          gap: 16 }}>
            {/* KPI side-by-side */}
            <div data-testid="comparison-kpi-table" style={{
              background: "#0b1220", border: "1px solid #1e293b",
              borderRadius: 8, padding: 16,
            }}>
              <h3 style={{ margin: 0, marginBottom: 10, fontSize: 13,
                              color: "#cbd5e1" }}>Key metrics</h3>
              <table style={{ width: "100%", fontSize: 12,
                                borderCollapse: "collapse" }}>
                <thead><tr style={{ color: "#94a3b8" }}>
                  <th style={{ textAlign: "left", padding: 6 }}>
                    Metric</th>
                  <th style={{ textAlign: "right", padding: 6 }}>
                    {labelFor(revA)}</th>
                  <th style={{ textAlign: "right", padding: 6 }}>
                    {labelFor(revB)}</th>
                  <th style={{ textAlign: "right", padding: 6 }}>Δ</th>
                  <th style={{ textAlign: "right", padding: 6 }}>%</th>
                </tr></thead>
                <tbody>
                  {comparison.kpi_rows.map((r, i) => (
                    <tr key={i} style={{
                      borderTop: "1px solid #1e293b",
                    }} data-testid={`comparison-kpi-${r.field}`}>
                      <td style={{ padding: 6 }}>{r.field}</td>
                      <td style={{ padding: 6, textAlign: "right",
                                      color: "#94a3b8" }}>
                        {_fmt(r.a)}</td>
                      <td style={{ padding: 6, textAlign: "right" }}>
                        {_fmt(r.b)}</td>
                      <td style={{ padding: 6, textAlign: "right",
                                      color: typeof r.delta === "number"
                                        ? (r.delta > 0 ? "#fbbf24"
                                          : r.delta < 0 ? "#67e8f9"
                                          : "#94a3b8")
                                        : "#94a3b8" }}>
                        {_fmt(r.delta)}</td>
                      <td style={{ padding: 6, textAlign: "right" }}>
                        <DeltaBadge pct={r.pct_change}/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cost side-by-side bars */}
            <div data-testid="comparison-cost-bars" style={{
              background: "#0b1220", border: "1px solid #1e293b",
              borderRadius: 8, padding: 16,
            }}>
              <h3 style={{ margin: 0, marginBottom: 10, fontSize: 13,
                              color: "#cbd5e1" }}>
                Cost breakdown (INR) ·
                {" "}<span style={{ color: "#fbbf24" }}>
                  Δ {_fmt(comparison.cost_total_delta)}
                </span>{" "}
                <DeltaBadge pct={comparison.cost_total_pct_change}/>
              </h3>
              {comparison.cost_rows.map((r, i) => {
                const max = Math.max(r.a, r.b, 1);
                return (
                  <div key={i}
                      data-testid={`comparison-cost-${r.section
                        .toLowerCase().replace(/\s+/g, "-")}`}
                      style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: 11, marginBottom: 3 }}>
                      <span style={{ color: "#cbd5e1" }}>{r.section}</span>
                      <span style={{ color: "#94a3b8" }}>
                        A {_fmt(r.a)} · B {_fmt(r.b)}{" "}
                        <DeltaBadge pct={r.pct_change}/>
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <div style={{ flex: 1,
                                      background: "#0f172a",
                                      borderRadius: 3,
                                      height: 8,
                                      overflow: "hidden" }}>
                        <div style={{
                          width: `${100 * r.a / max}%`,
                          height: "100%",
                          background: "linear-gradient("
                            + "90deg,#6366f1,#a78bfa)",
                        }}/>
                      </div>
                      <div style={{ flex: 1,
                                      background: "#0f172a",
                                      borderRadius: 3,
                                      height: 8,
                                      overflow: "hidden" }}>
                        <div style={{
                          width: `${100 * r.b / max}%`,
                          height: "100%",
                          background: "linear-gradient("
                            + "90deg,#0891b2,#22d3ee)",
                        }}/>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div style={{ display: "flex", gap: 16, marginTop: 4,
                              fontSize: 10, color: "#94a3b8" }}>
                <span><span style={{ display: "inline-block",
                                          width: 10, height: 10,
                                          background: "#a78bfa",
                                          borderRadius: 2,
                                          marginRight: 4,
                                          verticalAlign: -1 }}/>
                  Side A</span>
                <span><span style={{ display: "inline-block",
                                          width: 10, height: 10,
                                          background: "#22d3ee",
                                          borderRadius: 2,
                                          marginRight: 4,
                                          verticalAlign: -1 }}/>
                  Side B</span>
              </div>
            </div>
          </div>

          {/* Right column — compliance + stages */}
          <div style={{ display: "flex", flexDirection: "column",
                          gap: 16 }}>
            <div data-testid="comparison-compliance" style={{
              background: "#0b1220", border: "1px solid #1e293b",
              borderRadius: 8, padding: 16,
            }}>
              <h3 style={{ margin: 0, marginBottom: 10, fontSize: 13,
                              color: "#cbd5e1" }}>Compliance</h3>
              {[
                ["FSI",
                  comparison.compliance.a.fsi_status,
                  comparison.compliance.b.fsi_status,
                  `${comparison.compliance.a.fsi_actual} / `
                    + comparison.compliance.a.fsi_limit,
                  `${comparison.compliance.b.fsi_actual} / `
                    + comparison.compliance.b.fsi_limit],
                ["Coverage",
                  comparison.compliance.a.coverage_status,
                  comparison.compliance.b.coverage_status,
                  `${comparison.compliance.a.coverage_actual_pct}% / `
                    + `${comparison.compliance.a.coverage_limit_pct}%`,
                  `${comparison.compliance.b.coverage_actual_pct}% / `
                    + `${comparison.compliance.b.coverage_limit_pct}%`],
                ["Setbacks",
                  comparison.compliance.a.setback_status,
                  comparison.compliance.b.setback_status,
                  `${comparison.compliance.a.setback_violations} viol.`,
                  `${comparison.compliance.b.setback_violations} viol.`],
              ].map(([label, sa, sb, ta, tb]) => (
                <div key={label} style={{
                  display: "grid",
                  gridTemplateColumns: "60px 1fr 1fr",
                  gap: 8, padding: "6px 0",
                  borderTop: "1px solid #1e293b",
                  fontSize: 11,
                }}>
                  <span style={{ color: "#94a3b8" }}>{label}</span>
                  <span style={{
                    color: sa === "PASS" ? "#10b981" : "#ef4444",
                  }}>
                    {sa === "PASS"
                      ? <CheckCircle2 size={11}
                          style={{ verticalAlign: -1 }}/>
                      : <XCircle size={11}
                          style={{ verticalAlign: -1 }}/>}
                    {" "}{ta}
                  </span>
                  <span style={{
                    color: sb === "PASS" ? "#10b981" : "#ef4444",
                  }}>
                    {sb === "PASS"
                      ? <CheckCircle2 size={11}
                          style={{ verticalAlign: -1 }}/>
                      : <XCircle size={11}
                          style={{ verticalAlign: -1 }}/>}
                    {" "}{tb}
                  </span>
                </div>
              ))}
            </div>

            <div data-testid="comparison-stages" style={{
              background: "#0b1220", border: "1px solid #1e293b",
              borderRadius: 8, padding: 16,
            }}>
              <h3 style={{ margin: 0, marginBottom: 10, fontSize: 13,
                              color: "#cbd5e1" }}>Stage progress</h3>
              <div style={{ display: "grid",
                              gridTemplateColumns:
                                "repeat(12, minmax(0, 1fr))",
                              gap: 4 }}>
                {comparison.stage_rows.map((r) => (
                  <div key={r.stage}
                      data-testid={`comparison-stage-${r.stage}`}
                      style={{
                        textAlign: "center", padding: 4,
                        background: r.b_ran ? "#052e1644"
                                      : "#0f172a",
                        border: `1px solid ${
                          r.b_ran && r.a_ran ? "#15803d"
                          : r.b_ran && !r.a_ran ? "#facc15"
                          : r.a_ran && !r.b_ran ? "#dc2626"
                          : "#334155"}`,
                        borderRadius: 4,
                      }}>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>
                      {r.stage}
                    </div>
                    <div style={{ fontSize: 9,
                                    color: "#94a3b8" }}>
                      A:{r.a_ran ? "✓" : "·"} B:{r.b_ran ? "✓" : "·"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
